import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LuGripVertical, LuEye, LuEyeOff, LuX } from 'react-icons/lu';

const GLOBAL_STORAGE_KEY = 'omnivaso_draggable_toolbar_last_pos';

const getSavedPosition = (key) => {
    if (typeof window === 'undefined') return { x: 20, y: 75 };
    try {
        const specificKey = key ? `omnivaso_toolbar_pos_${key}` : null;
        if (specificKey && window.__lastToolbarPositions && window.__lastToolbarPositions[specificKey]) {
            return window.__lastToolbarPositions[specificKey];
        }
        if (window.__lastToolbarPositions && window.__lastToolbarPositions[GLOBAL_STORAGE_KEY]) {
            return window.__lastToolbarPositions[GLOBAL_STORAGE_KEY];
        }

        if (specificKey) {
            const savedSpecific = localStorage.getItem(specificKey);
            if (savedSpecific) {
                const parsed = JSON.parse(savedSpecific);
                if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') return parsed;
            }
        }

        const savedGlobal = localStorage.getItem(GLOBAL_STORAGE_KEY);
        if (savedGlobal) {
            const parsed = JSON.parse(savedGlobal);
            if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') return parsed;
        }
    } catch (e) {}

    const width = window.innerWidth;
    return {
        x: width < 640 ? 10 : Math.max(10, Math.min(60, width - 540)),
        y: 80
    };
};

const persistPosition = (pos, key) => {
    if (typeof window === 'undefined') return;
    window.__lastToolbarPositions = window.__lastToolbarPositions || {};
    const specificKey = key ? `omnivaso_toolbar_pos_${key}` : null;
    if (specificKey) {
        window.__lastToolbarPositions[specificKey] = pos;
        try { localStorage.setItem(specificKey, JSON.stringify(pos)); } catch (e) {}
    }
    window.__lastToolbarPositions[GLOBAL_STORAGE_KEY] = pos;
    try { localStorage.setItem(GLOBAL_STORAGE_KEY, JSON.stringify(pos)); } catch (e) {}
};

export const DraggableToolbar = ({ children, onClose, title = '⠿ Formatting Tools', toolbarId = null }) => {
    const toolbarRef = useRef(null);
    const key = toolbarId || (title ? title.replace(/[^a-zA-Z0-9_]/g, '_') : 'default');

    const [position, setPosition] = useState(() => getSavedPosition(key));
    const [isDragging, setIsDragging] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const clampPosition = (x, y) => {
        if (typeof window === 'undefined') return { x, y };
        const el = toolbarRef.current;
        const width = el ? el.offsetWidth : 480;
        const height = el ? el.offsetHeight : 100;
        const maxX = Math.max(8, window.innerWidth - width - 8);
        const maxY = Math.max(8, window.innerHeight - height - 8);

        return {
            x: Math.max(8, Math.min(x, maxX)),
            y: Math.max(8, Math.min(y, maxY))
        };
    };

    // Ensure toolbar is clamped immediately on mount and after render
    useLayoutEffect(() => {
        setPosition(prev => clampPosition(prev.x, prev.y));
    }, []);

    // Handle screen resize positioning constraints so it never gets clipped or pushed off screen
    useEffect(() => {
        const handleResize = () => {
            setPosition(prev => clampPosition(prev.x, prev.y));
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleMouseDown = (e) => {
        if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select')) return;
        setIsDragging(true);
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    };

    const handleTouchStart = (e) => {
        if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select')) return;
        if (e.touches && e.touches[0]) {
            setIsDragging(true);
            setDragStart({ x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y });
        }
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;
        setPosition(clampPosition(newX, newY));
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        if (e.touches && e.touches[0]) {
            e.preventDefault();
            const newX = e.touches[0].clientX - dragStart.x;
            const newY = e.touches[0].clientY - dragStart.y;
            setPosition(clampPosition(newX, newY));
        }
    };

    const handleMouseUp = () => {
        if (isDragging) {
            setIsDragging(false);
            setPosition(currentPos => {
                const clamped = clampPosition(currentPos.x, currentPos.y);
                persistPosition(clamped, key);
                return clamped;
            });
        }
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove, { passive: false });
            window.addEventListener('touchend', handleMouseUp);
            window.addEventListener('touchcancel', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleMouseUp);
            window.removeEventListener('touchcancel', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleMouseUp);
            window.removeEventListener('touchcancel', handleMouseUp);
        };
    }, [isDragging, dragStart]);

    return createPortal(
        <div 
            ref={toolbarRef}
            data-draggable-toolbar="true" 
            style={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                zIndex: 99999,
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(12px)',
                borderRadius: '10px',
                boxShadow: '0 12px 30px rgba(15, 23, 42, 0.25)',
                border: '1px solid #cbd5e1',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                width: isMinimized ? 'auto' : 'max-content',
                maxWidth: 'min(540px, calc(100vw - 16px))',
                boxSizing: 'border-box',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                transition: isDragging ? 'none' : 'box-shadow 0.2s ease, width 0.2s ease'
            }}
        >
            {/* TOOLBAR HEADER / DRAG HANDLE */}
            <div
                style={{
                    background: 'linear-gradient(135deg, #1e293b, #0f172a)',
                    color: '#ffffff',
                    padding: '7px 12px',
                    fontSize: '0.78rem',
                    fontWeight: '700',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    borderBottom: isMinimized ? 'none' : '1px solid #334155'
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', pointerEvents: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <LuGripVertical size={14} style={{ color: '#38bdf8', flexShrink: 0 }} />
                    <span>{typeof title === 'string' ? title.replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '').trim() : title}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
                        title={isMinimized ? 'Expand Tools' : 'Minimize / Hide Tools (Unobscure text)'}
                        style={{
                            background: isMinimized ? '#2563eb' : 'rgba(255, 255, 255, 0.15)',
                            border: 'none',
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontWeight: '700',
                            fontSize: '0.75rem',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                        }}
                    >
                        {isMinimized ? (
                            <>
                                <LuEye size={12} style={{ flexShrink: 0 }} />
                                <span>Show</span>
                            </>
                        ) : (
                            <>
                                <LuEyeOff size={12} style={{ flexShrink: 0 }} />
                                <span>Hide</span>
                            </>
                        )}
                    </button>
                    {onClose && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onClose(); }}
                            title="Close Tools"
                            style={{
                                background: 'rgba(239, 68, 68, 0.2)',
                                border: 'none',
                                color: '#fca5a5',
                                cursor: 'pointer',
                                fontWeight: '700',
                                fontSize: '0.85rem',
                                width: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 1,
                                padding: 0
                            }}
                        >
                            <LuX size={13} style={{ flexShrink: 0 }} />
                        </button>
                    )}
                </div>
            </div>

            {/* TOOLBAR CONTENT BODY */}
            {!isMinimized && (
                <div style={{
                    padding: '8px 10px',
                    cursor: 'default',
                    maxHeight: '75vh',
                    overflowY: 'auto',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    boxSizing: 'border-box'
                }}>
                    {children}
                </div>
            )}
        </div>,
        document.body
    );
};
export default DraggableToolbar;
